// components/reports/TablesReport.jsx
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Badge } from '../../../../components/ui/badge';
import { Clock, CheckCircle, XCircle, Table as TableIcon } from 'lucide-react';

export function TablesReport({ data }) {
  if (!data || !data.Table15) return null;

  const tables = data.Table15;
  const uniqueTables = Array.from(new Set(tables.map(t => t.TableId)))
    .map(id => tables.find(t => t.TableId === id))
    .filter(Boolean);

  const openTables = uniqueTables.filter(table => table.IsOpen);
  const closedTables = uniqueTables.filter(table => !table.IsOpen);
  
  // Group by floor/area (assuming TableName like F1, F2 for floor)
  const floorGroups = {};
  uniqueTables.forEach(table => {
    const floor = table.TableName?.charAt(0) || 'Unknown';
    if (!floorGroups[floor]) floorGroups[floor] = [];
    floorGroups[floor].push(table);
  });

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tables</CardTitle>
            <TableIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {uniqueTables.length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Open Tables</CardTitle>
            <Clock className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {openTables.length}
            </div>
            <p className="text-xs text-muted-foreground">
              {openTables.length > 0 ? `${openTables[0]?.OpenDurationMinutes || 0} min avg` : 'No open tables'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Closed Tables</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {closedTables.length}
            </div>
            <p className="text-xs text-muted-foreground">
              {closedTables.length} available
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Occupancy Rate</CardTitle>
            <XCircle className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {Math.round((openTables.length / uniqueTables.length) * 100)}%
            </div>
            <p className="text-xs text-muted-foreground">
              Current occupancy
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Floor-wise View */}
      <Card>
        <CardHeader>
          <CardTitle>Floor-wise Table Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {Object.entries(floorGroups).map(([floor, floorTables]) => (
              <div key={floor}>
                <h3 className="text-lg font-semibold mb-4">Floor {floor}</h3>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                  {floorTables.map((table, index) => (
                    <div 
                      key={index}
                      className={`p-4 rounded-lg border-2 ${table.IsOpen ? 'border-yellow-500 bg-yellow-50' : 'border-green-200 bg-green-50'}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${table.IsOpen ? 'bg-yellow-500 animate-pulse' : 'bg-green-500'}`} />
                          <span className="font-bold text-lg">{table.TableName}</span>
                        </div>
                        <Badge variant={table.IsOpen ? "default" : "outline"}>
                          {table.IsOpen ? 'OPEN' : 'CLOSED'}
                        </Badge>
                      </div>
                      
                      {table.IsOpen ? (
                        <>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Open Duration:</span>
                              <span className="font-medium">{table.OpenDurationMinutes} min</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Bills Printed:</span>
                              <span className="font-medium">{table.BillPrintCount}</span>
                            </div>
                            {table.OrderNumber && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Order:</span>
                                <span className="font-medium truncate max-w-[100px]">
                                  {table.OrderNumber}
                                </span>
                              </div>
                            )}
                            {table.OpenedDateTime && (
                              <div className="text-xs text-muted-foreground">
                                Since {new Date(table.OpenedDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            )}
                          </div>
                          <div className="mt-4 pt-3 border-t">
                            <button className="w-full py-2 text-sm bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-colors">
                              View Details
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-4">
                          <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                          <p className="text-sm text-muted-foreground">Available</p>
                          <button className="mt-3 w-full py-2 text-sm bg-green-500 text-white rounded hover:bg-green-600 transition-colors">
                            Assign Table
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Detailed Table Logs */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Table Activities</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-medium">Table</th>
                  <th className="text-left py-3 px-4 font-medium">Status</th>
                  <th className="text-left py-3 px-4 font-medium">Open Time</th>
                  <th className="text-left py-3 px-4 font-medium">Duration</th>
                  <th className="text-left py-3 px-4 font-medium">Order Number</th>
                  <th className="text-left py-3 px-4 font-medium">Bills Printed</th>
                  <th className="text-left py-3 px-4 font-medium">Last Activity</th>
                </tr>
              </thead>
              <tbody>
                {tables.slice(0, 10).map((table, index) => (
                  <tr key={index} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">{table.TableName}</td>
                    <td className="py-3 px-4">
                      <Badge 
                        className={table.IsOpen ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}
                      >
                        {table.IsOpen ? 'OPEN' : 'CLOSED'}
                      </Badge>
                    </td>
                    <td className="py-3 px-4">
                      {table.OpenedDateTime 
                        ? new Date(table.OpenedDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                        : '-'}
                    </td>
                    <td className="py-3 px-4">
                      {table.OpenDurationMinutes ? `${table.OpenDurationMinutes} min` : '-'}
                    </td>
                    <td className="py-3 px-4">
                      {table.OrderNumber || '-'}
                    </td>
                    <td className="py-3 px-4">{table.BillPrintCount}</td>
                    <td className="py-3 px-4">
                      {table.BillPrintDateTime 
                        ? new Date(table.BillPrintDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}